import { useCallback } from "react";
import { MutableRefObject } from "react";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { Request } from "../Utilities/mappers";
import { UseRequestApi } from "../Api/RequestApi";
import { ModalProviderState, Snackbar, SnackbarType } from "vig-ui-library";
import { DeleteUploadedFileInputSchema } from "../Schemas/DeleteUploadedFileInput";
import { getRequestWithFiles } from "../Utilities/mappers";

type UseDeleteFileOptions = {
    file: RequestFileDto;
    request: MutableRefObject<Request>;
    requestApi: UseRequestApi;
    openConfirmModal: ModalProviderState["openConfirmModal"];
    openSnackbar: (snackbar: Snackbar) => void;
    rerenderForm: () => void;
    removeFileStrategy?: "index" | "id";
    fileIndex?: number;
};

export const useDeleteFile = (options: UseDeleteFileOptions) => {
    const {
        file,
        request,
        requestApi,
        openConfirmModal,
        openSnackbar,
        rerenderForm,
        removeFileStrategy = "id",
        fileIndex,
    } = options;

    const handleDelete = useCallback(() => {
        const input = DeleteUploadedFileInputSchema.parse({
            fileId: file.id,
            requestId: request.current.requestId,
        });

        openConfirmModal({
            title: `Naozaj si želáte zmazať súbor "${file.name}"?`,
            description: "Po zmazaní bude súbor odstránený zo žiadosti",
            confirmText: "Zmazať",
            cancelText: "Zrušiť",
            onConfirm: async () => {
                try {
                    await requestApi.deleteUploadedFile(input);
                    openSnackbar({
                        text: "Súbor bol vymazaný",
                        type: SnackbarType.SUCCESS,
                    });

                    // Remove file from request based on strategy
                    if (removeFileStrategy === "index" && fileIndex !== undefined) {
                        request.current.files?.splice(fileIndex, 1);
                    } else {
                        const requestWithFiles = getRequestWithFiles(request);
                        const index = requestWithFiles.current.files.findIndex(f => f.id === file.id);
                        if (index >= 0) {
                            requestWithFiles.current.files.splice(index, 1);
                        }
                    }

                    rerenderForm();
                } catch (err) {
                    openSnackbar({
                        text: "Nastala chyba pri mazaní súboru",
                        type: SnackbarType.ERROR,
                    });
                }
            }
        });
    }, [file, request, requestApi, openConfirmModal, openSnackbar, rerenderForm, removeFileStrategy, fileIndex]);

    return { handleDelete };
};

